import { format } from 'date-fns'
import { useSelector } from 'react-redux'

import { useGetRouteOptimizationJobQuery } from 'api/route/routeOptimizationJob'
import { selectOptimizationJobContext } from 'slices/route/persistentOptimizationJobsSlice'
import { ROUTE_OPTIMIZATION_SCOPE } from 'types/api/route/routeOptimizationJob'
import { NOTIFICATION_SOURCE, NOTIFICATION_STATUS } from 'types/modules/notification-center/notification'

import { urlForEntity } from './notificationCenterViews'
import { optimizationOptionsUsed } from './optimizationOptionsUsed'

import type { Notification } from 'types/modules/notification-center/notification'

/** The lowercase weekday `day_of_week` carries; the board opens on its default without it. */
export const dayOfWeekOf = (serviceDate: string) => format(new Date(`${serviceDate}T00:00:00`), 'EEEE').toLowerCase()

export const OPTIMIZATION_OUTCOME = {
  APPLIED: 'APPLIED',
  REJECTED: 'REJECTED',
  PENDING: 'PENDING',
} as const

export type OptimizationOutcome = typeof OPTIMIZATION_OUTCOME[keyof typeof OPTIMIZATION_OUTCOME]

export interface OptimizationReview {
  /** Where the run's comparison reopens, or undefined when the notification cannot describe one. */
  reviewUrl?: string
  /** A master run lands on master and every future day; a daily one on its own day. */
  isMasterRun: boolean
  /** True once its comparison was accepted, so the run is spent. */
  isApplied: boolean
  isRejected: boolean
  /** Undecided is a state of its own: a finished run nobody has answered yet still wants an answer. */
  outcome?: OptimizationOutcome
  serviceDate?: string
  /** The run's own options, when this browser submitted it; nothing on the wire carries them. */
  optionsUsed?: string
}

const NOT_REVIEWABLE: OptimizationReview = { isMasterRun: false, isApplied: false, isRejected: false }

/**
 * Route, day and scope ride on the payload; the outcome does not, because the notification is written
 * when the run finishes and answered later — so a payload without one is read back off the job.
 */
export const useOptimizationReview = (notification: Notification): OptimizationReview => {
  const { jobId, scope, resolution } = notification.payload ?? {}
  const remembered = useSelector(selectOptimizationJobContext(jobId ?? null))

  const isReviewable =
    notification.source === NOTIFICATION_SOURCE.ROUTE_OPTIMIZATION && notification.status === NOTIFICATION_STATUS.SUCCEEDED

  const { data: job } = useGetRouteOptimizationJobQuery(jobId ?? '', { skip: !isReviewable || !jobId || Boolean(resolution) })

  if (!isReviewable) return NOT_REVIEWABLE

  const serviceDate = notification.payload?.serviceDate ?? remembered?.serviceDate
  if (!serviceDate) return NOT_REVIEWABLE

  const isMasterRun = scope ? scope === ROUTE_OPTIMIZATION_SCOPE.MASTER : Boolean(remembered?.isMasterRun)
  const settled = resolution ?? job?.resolution
  // A local mark still counts: it is recorded the moment this browser applies, before the job re-reads.
  const isApplied = settled === 'APPLIED' || Boolean(remembered?.appliedAt)
  const isRejected = settled === 'REJECTED'

  let outcome: OptimizationOutcome = OPTIMIZATION_OUTCOME.PENDING
  if (isApplied) outcome = OPTIMIZATION_OUTCOME.APPLIED
  else if (isRejected) outcome = OPTIMIZATION_OUTCOME.REJECTED

  return {
    reviewUrl: urlForEntity(notification.entityType, notification.entityId, jobId, dayOfWeekOf(serviceDate)),
    isMasterRun,
    isApplied,
    isRejected,
    outcome,
    serviceDate,
    optionsUsed: optimizationOptionsUsed(remembered),
  }
}

/**
 * Applied is spent, so its action recedes to text; rejected is still openable but is no longer the
 * thing to do next, so it outlines rather than filling. Only an undecided run asks for a press.
 */
export const reviewActionVariant = ({ isApplied, isRejected }: Pick<OptimizationReview, 'isApplied' | 'isRejected'>) => {
  if (isApplied) return 'text' as const
  if (isRejected) return 'outlined' as const
  return 'contained' as const
}
